export interface LanguageInfo {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
  region?: string;
}

export const supportedLanguages: LanguageInfo[] = [
  { 
    code: 'en', 
    name: 'English', 
    nativeName: 'English',
    flag: '🇺🇸',
    region: 'US'
  },
  { 
    code: 'en-IN', 
    name: 'Indian English', 
    nativeName: 'English (India)',
    flag: '🇮🇳',
    region: 'IN'
  },
  { 
    code: 'es', 
    name: 'Spanish', 
    nativeName: 'Español',
    flag: '🇪🇸'
  },
  { 
    code: 'te', 
    name: 'Telugu', 
    nativeName: 'తెలుగు',
    flag: '🇮🇳'
  },
  { 
    code: 'hi', 
    name: 'Hindi', 
    nativeName: 'हिंदी',
    flag: '🇮🇳'
  },
];

// Map of country codes to preferred languages
const countryLanguageMap: { [key: string]: string } = {
  'IN': 'hi', // India - Hindi
  'ES': 'es', // Spain - Spanish
  'MX': 'es', // Mexico - Spanish
  'AR': 'es', // Argentina - Spanish
  'CO': 'es', // Colombia - Spanish
  'PE': 'es', // Peru - Spanish
  'VE': 'es', // Venezuela - Spanish
  'CL': 'es', // Chile - Spanish
  'EC': 'es', // Ecuador - Spanish
  'GT': 'es', // Guatemala - Spanish
  'CU': 'es', // Cuba - Spanish
  'BO': 'es', // Bolivia - Spanish
  'DO': 'es', // Dominican Republic - Spanish
  'HN': 'es', // Honduras - Spanish
  'PY': 'es', // Paraguay - Spanish
  'SV': 'es', // El Salvador - Spanish
  'NI': 'es', // Nicaragua - Spanish
  'CR': 'es', // Costa Rica - Spanish
  'PA': 'es', // Panama - Spanish
  'UY': 'es', // Uruguay - Spanish
  'GQ': 'es', // Equatorial Guinea - Spanish
  'PH': 'en', // Philippines - English
  'SG': 'en', // Singapore - English
  'MY': 'en', // Malaysia - English
  'HK': 'en', // Hong Kong - English
  'AE': 'en', // UAE - English
  'SA': 'en', // Saudi Arabia - English
  'QA': 'en', // Qatar - English
  'KW': 'en', // Kuwait - English
  'BH': 'en', // Bahrain - English
  'OM': 'en', // Oman - English
  'JO': 'en', // Jordan - English
  'LB': 'en', // Lebanon - English
  'IL': 'en', // Israel - English
  'ZA': 'en', // South Africa - English
  'NG': 'en', // Nigeria - English
  'KE': 'en', // Kenya - English
  'UG': 'en', // Uganda - English
  'TZ': 'en', // Tanzania - English
  'GH': 'en', // Ghana - English
  'ZM': 'en', // Zambia - English
  'ZW': 'en', // Zimbabwe - English
  'BW': 'en', // Botswana - English
  'MW': 'en', // Malawi - English
  'NA': 'en', // Namibia - English
  'SZ': 'en', // Eswatini - English
  'LS': 'en', // Lesotho - English
  'MU': 'en', // Mauritius - English
  'SC': 'en', // Seychelles - English
  'FJ': 'en', // Fiji - English
  'PG': 'en', // Papua New Guinea - English
  'VU': 'en', // Vanuatu - English
  'SB': 'en', // Solomon Islands - English
  'TO': 'en', // Tonga - English
  'WS': 'en', // Samoa - English
  'KI': 'en', // Kiribati - English
  'TV': 'en', // Tuvalu - English
  'NR': 'en', // Nauru - English
  'PW': 'en', // Palau - English
  'MH': 'en', // Marshall Islands - English
  'FM': 'en', // Micronesia - English
  'CK': 'en', // Cook Islands - English
  'NU': 'en', // Niue - English
  'TK': 'en', // Tokelau - English
  'AS': 'en', // American Samoa - English
  'GU': 'en', // Guam - English
  'MP': 'en', // Northern Mariana Islands - English
  'VI': 'en', // U.S. Virgin Islands - English
  'PR': 'en', // Puerto Rico - English
  'AI': 'en', // Anguilla - English
  'VG': 'en', // British Virgin Islands - English
  'KY': 'en', // Cayman Islands - English
  'TC': 'en', // Turks and Caicos Islands - English
  'BM': 'en', // Bermuda - English
  'FK': 'en', // Falkland Islands - English
  'GI': 'en', // Gibraltar - English
  'IO': 'en', // British Indian Ocean Territory - English
  'PN': 'en', // Pitcairn Islands - English
  'SH': 'en', // Saint Helena - English
  'GS': 'en', // South Georgia and South Sandwich Islands - English
  'AC': 'en', // Ascension Island - English
  'TA': 'en', // Tristan da Cunha - English
  'US': 'en', // United States - English
  'CA': 'en', // Canada - English
  'GB': 'en', // United Kingdom - English
  'AU': 'en', // Australia - English
  'NZ': 'en', // New Zealand - English
  'IE': 'en', // Ireland - English
  'MT': 'en', // Malta - English
  'CY': 'en', // Cyprus - English
  'JM': 'en', // Jamaica - English
  'BB': 'en', // Barbados - English
  'TT': 'en', // Trinidad and Tobago - English
  'GD': 'en', // Grenada - English
  'LC': 'en', // Saint Lucia - English
  'VC': 'en', // Saint Vincent and the Grenadines - English
  'AG': 'en', // Antigua and Barbuda - English
  'KN': 'en', // Saint Kitts and Nevis - English
  'DM': 'en', // Dominica - English
  'BS': 'en', // Bahamas - English
  'BZ': 'en', // Belize - English
  'GY': 'en', // Guyana - English
  'SR': 'en', // Suriname - English
  'GF': 'en', // French Guiana - English
};

export const getLanguageInfo = (code: string): LanguageInfo | undefined => {
  return supportedLanguages.find(lang => lang.code === code);
};

export const getLanguageName = (code: string): string => {
  const lang = getLanguageInfo(code);
  return lang ? lang.nativeName : code;
};

export const isRTL = (languageCode: string): boolean => {
  // Currently no RTL languages supported, but this can be extended
  return false;
};

export const formatNumber = (value: number, languageCode: string): string => {
  try {
    return new Intl.NumberFormat(languageCode).format(value);
  } catch {
    return value.toString();
  }
};

export const formatDate = (date: Date, languageCode: string): string => {
  try {
    return new Intl.DateTimeFormat(languageCode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
};

export const formatShortDate = (date: Date): string => {
  const day = date.getDate();
  const month = date.toLocaleDateString('en', { month: 'short' });
  
  // Add ordinal suffix to day
  const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  const suffix = getOrdinalSuffix(day);
  return `${day}<sup>${suffix}</sup> ${month}`;
};

export const formatCurrency = (amount: number, currency: string, languageCode: string): string => {
  try {
    return new Intl.NumberFormat(languageCode, {
      style: 'currency',
      currency: currency
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

// Enhanced language detection with location-based fallback
export const detectUserLanguage = (): string => {
  // Check localStorage first (user preference)
  const savedLanguage = localStorage.getItem('preferredLanguage');
  if (savedLanguage && supportedLanguages.some(lang => lang.code === savedLanguage)) {
    return savedLanguage;
  }

  // Check browser language
  const browserLanguage = navigator.language.split('-')[0];
  if (supportedLanguages.some(lang => lang.code === browserLanguage)) {
    return browserLanguage;
  }

  // Check browser languages array
  if (navigator.languages) {
    for (const lang of navigator.languages) {
      const langCode = lang.split('-')[0];
      if (supportedLanguages.some(lang => lang.code === langCode)) {
        return langCode;
      }
    }
  }

  // Try to detect country from browser locale
  const fullLocale = navigator.language;
  const countryCode = fullLocale.split('-')[1]?.toUpperCase();
  
  if (countryCode && countryLanguageMap[countryCode]) {
    const preferredLanguage = countryLanguageMap[countryCode];
    if (supportedLanguages.some(lang => lang.code === preferredLanguage)) {
      return preferredLanguage;
    }
  }

  // Default to US English
  return 'en';
};

// Function to get user's country (for future use)
export const getUserCountry = async (): Promise<string | null> => {
  try {
    // Try to get country from IP geolocation
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return data.country_code || null;
  } catch (error) {
    console.warn('Could not detect user country:', error);
    return null;
  }
};

// Enhanced language detection with IP geolocation
export const detectUserLanguageWithLocation = async (): Promise<string> => {
  // Check localStorage first (user preference)
  const savedLanguage = localStorage.getItem('preferredLanguage');
  if (savedLanguage && supportedLanguages.some(lang => lang.code === savedLanguage)) {
    return savedLanguage;
  }

  // Check browser language
  const browserLanguage = navigator.language.split('-')[0];
  if (supportedLanguages.some(lang => lang.code === browserLanguage)) {
    return browserLanguage;
  }

  // Check browser languages array
  if (navigator.languages) {
    for (const lang of navigator.languages) {
      const langCode = lang.split('-')[0];
      if (supportedLanguages.some(lang => lang.code === langCode)) {
        return langCode;
      }
    }
  }

  // Try to get country from IP geolocation
  try {
    const countryCode = await getUserCountry();
    if (countryCode && countryLanguageMap[countryCode]) {
      const preferredLanguage = countryLanguageMap[countryCode];
      if (supportedLanguages.some(lang => lang.code === preferredLanguage)) {
        return preferredLanguage;
      }
    }
  } catch (error) {
    console.warn('Could not detect user location:', error);
  }

  // Try to detect country from browser locale
  const fullLocale = navigator.language;
  const localeCountryCode = fullLocale.split('-')[1]?.toUpperCase();
  
  if (localeCountryCode && countryLanguageMap[localeCountryCode]) {
    const preferredLanguage = countryLanguageMap[localeCountryCode];
    if (supportedLanguages.some(lang => lang.code === preferredLanguage)) {
      return preferredLanguage;
    }
  }

  // Default to US English
  return 'en';
}; 